import { z } from 'zod';

import { SOCIAL_PROVIDERS, type SocialProvider } from './social-handoff';

const STORAGE_KEY = 'autop-last-signin';

/** 로그인 수단. 이메일은 제공사가 아니라 우리 폼이라 소셜 목록 밖에 따로 둔다. */
export const EMAIL_SIGNIN = 'email';

export type SigninMethod = SocialProvider | typeof EMAIL_SIGNIN;

const methodSchema = z.enum([...SOCIAL_PROVIDERS, EMAIL_SIGNIN]);

/**
 * 마지막으로 로그인에 성공한 수단.
 *
 * 이메일이 다르면 다른 회원이므로, 카카오와 네이버로 각각 들어온 사람은 서로 다른 계정을
 * 갖게 된다. 그때 "어느 문으로 들어왔더라"를 기억하지 못하면 사용자는 자기 자료가 사라진
 * 것처럼 느낀다 — 그 혼동을 막는 표식이다.
 *
 * **수단만 남기고 이메일은 남기지 않는다.** 공용 PC 에서 다음 사람에게 보이면 안 되는 것은
 * 주소이지 "카카오를 썼다"가 아니다. 로그아웃·새로고침과 무관하게 남아야 하므로
 * 세션 저장소가 아니라 localStorage 를 쓴다.
 *
 * **이메일 로그인도 남긴다.** 안 남기면 소셜로 한 번 들어왔던 사람이 그 뒤로 이메일만
 * 써도 소셜 버튼에 「마지막 로그인」 표식이 계속 붙어 있어, 표식이 거짓말을 한다.
 *
 * 로그인 여부를 판단하는 근거가 아니다. 버튼에 무엇을 표시할지만 정한다.
 */
export const rememberLastSignin = (method: SigninMethod): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, method);
  } catch {
    // 저장소가 막힌 브라우저가 있다. 표식이 없으면 버튼이 평소대로 보일 뿐이다.
  }
};

export const readLastSignin = (): SigninMethod | null => {
  const parsed = methodSchema.safeParse(readRaw());
  return parsed.success ? parsed.data : null;
};

const readRaw = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};
